import React, { useEffect, useState, useMemo } from "react";
import { Box } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Loading from "../../utils/Loader";
import LibraryTabContent from "./LibraryTabContent";
import { LIBRARY_CONSTANT } from "../../constant/constant";
import { useSelector } from "react-redux";
import { darkTheme, lightTheme } from "../../theme/Theme";
import NoRecordFounds from "../../utils/NoRecordsFound";

export default function TabContentView({
  tabData,
  selectedView,
  searchText,
  cropType,
  countryFilter,
  languageFilter,
  localSector,
}) {
  const [isTabLoading, setLoading] = useState(false);
  const [value, setValue] = useState(LIBRARY_CONSTANT.all);
  const isDarkTheme = useSelector((state) => state.theme);
  const [itemCounts, setItemCounts] = useState({
    all: 0,
    documents: 0,
    videos: 0,
    links: 0,
  });

  // Redux selectors for filtering

  // Compute filtered data based on search and filters
  const filteredPageData = useMemo(() => {
    if (!tabData || !tabData.subjectItems) return []; // Return empty if tabData isn't ready

    const filterData = (items) =>
      items?.filter((item) => {
        const matchesSearch = Object.values(item)?.some((value) =>
          String(value)
            ?.toLowerCase()
            ?.includes(searchText?.toLowerCase() || "")
        );

        const matchCrop =
          !cropType ||
          item?.cropType?.toLowerCase() === cropType?.label.toLowerCase();

        const matchesCountry =
          !countryFilter ||
          item?.countries?.some(
            (lang) =>
              lang?.name &&
              lang.name.toLowerCase() === countryFilter.label.toLowerCase()
          );

        const matchesLanguage =
          !languageFilter ||
          item?.languages?.some(
            (lang) =>
              lang?.name &&
              lang.name.toLowerCase() === languageFilter.label.toLowerCase()
          );

        const matchesSector =
          !localSector ||
          item?.sectors?.some(
            (lang) =>
              lang?.name &&
              lang.name.toLowerCase() === localSector.label.toLowerCase()
          );

        return (
          matchesSearch &&
          matchesCountry &&
          matchesLanguage &&
          matchesSector &&
          matchCrop
        );
      });

    return tabData.subjectItems.map((section) => ({
      ...section,
      documents: filterData(section.documents),
      links: filterData(section.links),
      videos: filterData(section.videos),
    }));
  }, [
    tabData,
    searchText,
    countryFilter,
    languageFilter,
    localSector,
    cropType,
  ]);

  // Compute counts dynamically based on filtered data
  const filteredItemCounts = useMemo(() => {
    let newCountObj = {
      all: 0,
      documents: 0,
      videos: 0,
      links: 0,
    };

    filteredPageData?.forEach((section) => {
      newCountObj.documents += section.documents.length;
      newCountObj.links += section.links.length;
      newCountObj.videos += section.videos.length;
      newCountObj.all +=
        section.documents.length + section.links.length + section.videos.length;
    });

    return newCountObj;
  }, [filteredPageData]);

  // Update counts dynamically when filters change
  useEffect(() => {
    setItemCounts(filteredItemCounts);
  }, [filteredItemCounts]);

  // Handle Tab Change
  const handleChange = (event, tabType) => {
    setLoading(true);
    setValue(tabType);
    setTimeout(() => {
      setLoading(false);
    }, 1500);
  };

  // Render Tab Content
  const renderTabContentHtml = () => {
    let hasRecords = false;

    const filteredSections = filteredPageData?.map((section) => {
      let filteredDocuments =
        value === LIBRARY_CONSTANT.all || value === LIBRARY_CONSTANT.document
          ? section.documents
          : [];

      let filteredVideos =
        value === LIBRARY_CONSTANT.all || value === LIBRARY_CONSTANT.videos
          ? section.videos
          : [];

      let filteredLinks =
        value === LIBRARY_CONSTANT.all || value === LIBRARY_CONSTANT.links
          ? section.links
          : [];

      // Check if there are records for this section
      if (
        filteredDocuments.length > 0 ||
        filteredVideos.length > 0 ||
        filteredLinks.length > 0
      ) {
        hasRecords = true;
      }

      return {
        ...section,
        documents: filteredDocuments,
        videos: filteredVideos,
        links: filteredLinks,
      };
    });

    return hasRecords ? (
      filteredSections.map((section) => (
        <LibraryTabContent
          key={section.id}
          data={section}
          currentTab={value}
          grid={selectedView}
        />
      ))
    ) : (
      <NoRecordFounds />
    );
  };

  // Show Loader until tabData is available
  if (!tabData || !tabData.subjectItems) {
    return <Loading />;
  }

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={value} onChange={handleChange} aria-label="library-tabs">
          <Tab
            label={`All (${filteredItemCounts.all})`}
            value={LIBRARY_CONSTANT.all}
            sx={{ fontFamily: "Inter", textTransform: "none" }}
            style={{
              color: isDarkTheme
                ? darkTheme.textColorLayer4
                : lightTheme.textColorLayer4,
              fontWeight: value === LIBRARY_CONSTANT.all ? 700 : 400,
            }}
          />
          <Tab
            label={`Documents (${filteredItemCounts.documents})`}
            value={LIBRARY_CONSTANT.document}
            sx={{ fontFamily: "Inter", textTransform: "none" }}
            style={{
              color: isDarkTheme
                ? darkTheme.textColorLayer4
                : lightTheme.textColorLayer4,
              fontWeight: value === LIBRARY_CONSTANT.document ? 700 : 400,
            }}
          />
          <Tab
            label={`Videos (${filteredItemCounts.videos})`}
            value={LIBRARY_CONSTANT.videos}
            sx={{ fontFamily: "Inter", textTransform: "none" }}
            style={{
              color: isDarkTheme
                ? darkTheme.textColorLayer4
                : lightTheme.textColorLayer4,
              fontWeight: value === LIBRARY_CONSTANT.videos ? 700 : 400,
            }}
          />
          <Tab
            label={`Links (${filteredItemCounts.links})`}
            value={LIBRARY_CONSTANT.links}
            sx={{ fontFamily: "Inter", textTransform: "none" }}
            style={{
              color: isDarkTheme
                ? darkTheme.textColorLayer4
                : lightTheme.textColorLayer4,
              fontWeight: value === LIBRARY_CONSTANT.links ? 700 : 400,
            }}
          />
        </Tabs>
      </Box>
      {isTabLoading ? (
        <Loading />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            marginBottom: "25px",
            borderRadius: "16px",
            boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.2)",
          }}
        >
          {renderTabContentHtml()}
        </Box>
      )}
    </>
  );
}
